type PortfolioTreeNode = { id: number; children?: PortfolioTreeNode[] };

type ShouldKeep<T extends PortfolioTreeNode> = (portfolio: T, path: number[]) => boolean;
type FlattenPortfolio<T extends PortfolioTreeNode> = (portfolio: T, path: number[]) => T[];

/**
 * Rebuilds a portfolio tree based on `shouldKeep` and `flattenPortfolio`.
 *
 * Rules:
 * - If shouldKeep(node, path) === true:
 *     - Recurse into its children and keep the node, with pruned children.
 * - If shouldKeep(node, path) === false:
 *     - Replace this node with the result of flattenPortfolio(node, path).
 *
 * `path` is an array of ancestor ids from root to parent of `node`.
 */
export const filterPortfolioTreeLevels = <T extends PortfolioTreeNode>(
  portfolios: T[],
  shouldKeep: ShouldKeep<T>,
  flattenPortfolio: FlattenPortfolio<T>,
): T[] => {
  const processLevel = (nodes: T[], path: number[]): T[] => {
    const result: T[] = [];

    nodes.forEach((node) => {
      const nodePath = [...path, node.id];

      if (shouldKeep(node, path)) {
        // Keep this node; process its children with updated path (node becomes parent)
        const processedChildren = node.children ? processLevel(node.children as T[], nodePath) : undefined;

        result.push({
          ...node,
          children: processedChildren,
        } as T);
      } else {
        // Do not keep this node; flatten it into replacement nodes at the same level (same path)
        const replacements = flattenPortfolio(node, path) || [];
        if (!replacements.length) {
          // Nothing to add in place of this node
          return;
        }

        // Recurse over replacements at the same depth and same path
        const processedReplacements = processLevel(replacements, nodePath);
        result.push(...processedReplacements);
      }
    });

    return result;
  };

  return processLevel(portfolios, []);
};

/**
 * Groups siblings by a key and reduces each group with `mergePortfolios`.
 * The children's arrays of grouped nodes are concatenated and merged recursively top-down.
 */
export const mergePortfolioTreeTopDown = <T extends { children?: T[] }>(
  portfolios: T[],
  keyFn: (node: T) => string,
  mergePortfolios: (a: T, b: T) => T,
): T[] => {
  type Item = { node: T; idx: number };

  const mergeSiblingsByKey = (nodes: T[]): T[] => {
    if (!nodes || nodes.length <= 1) {
      return nodes;
    }

    // Pair each node with its original index at this level
    const itemsWithIndex: Item[] = nodes.map((node, i) => ({ node, idx: i }));

    // Group by key
    const grouped: Record<string, Item[]> = itemsWithIndex.reduce<Record<string, Item[]>>((acc, item) => {
      const k = keyFn(item.node);
      (acc[k] ||= []).push(item);

      return acc;
    }, {});

    // Reduce each group after sorting by original index
    return Object.values(grouped).map((group) => {
      if (group.length === 1) {
        // Return the solo item; children will be processed in recursion
        return group[0]!.node;
      }

      // Sort by original index to make merging deterministic
      group.sort((a, b) => a.idx - b.idx);

      // Extract nodes in stable order
      const orderedNodes = group.map((g) => g.node);

      // Reduce fields with mergePortfolios
      return orderedNodes.slice(1).reduce<T>((acc, cur) => {
        // mergePortfolios should also merge children
        return mergePortfolios(acc, cur);
      }, orderedNodes[0]!);
    });
  };

  const mergeLevelTopDown = (nodes: T[]): T[] => {
    const mergedAtLevel = mergeSiblingsByKey(nodes);

    return mergedAtLevel.map((node) => {
      return {
        ...node,
        children: node.children?.length ? mergeLevelTopDown(node.children) : node.children,
      };
    });
  };

  return mergeLevelTopDown(portfolios);
};
